// The narration engine on the web (NARRATION.md, simplified for a browser tab): a part is narrated as ≤700-char
// segments, at most 3 Gemini / 8 OpenRouter / 4 OpenAI requests in flight, playback's part first; retries with
// backoff on network and 5xx, a cooldown on 429, refused passages split in half down to 150 characters. The finished
// part (PCM + estimated word timing) is cached in IndexedDB so it plays again offline and is never paid for twice.
import * as db from "../store/db";
import { settings, type NarrationVoice } from "../settings";
import { isAbort, sha1, sleep } from "../util";
import { bisect, segments } from "./chunker";
import { narrate, RATE, TTSFailure } from "./tts";
import { partTiming, type TimingMap } from "./wordTiming";

export interface PartAudio { pcm: Int16Array; timing: TimingMap; rate: number }
interface Stored { pcm: ArrayBuffer; timing: TimingMap; rate: number }

const LIMITS: Record<NarrationVoice["provider"], number> = { gemini: 3, openrouter: 8, openai: 4 };

class Slots {
  private busy: Record<string, number> = { gemini: 0, openrouter: 0, openai: 0 };
  private waiters: { provider: string; urgent: boolean; go: () => void }[] = [];
  cooldownUntil: Record<string, number> = {};

  async acquire(provider: string, urgent: boolean, signal: AbortSignal): Promise<void> {
    const wait = (this.cooldownUntil[provider] ?? 0) - Date.now();
    if (wait > 0) await sleep(wait, signal);
    if (this.busy[provider] < LIMITS[provider as NarrationVoice["provider"]]) { this.busy[provider]++; return; }
    await new Promise<void>((resolve, reject) => {
      const w = { provider, urgent, go: () => { this.busy[provider]++; resolve(); } };
      if (urgent) this.waiters.unshift(w); else this.waiters.push(w);
      signal.addEventListener("abort", () => { this.waiters = this.waiters.filter((x) => x !== w); reject(new DOMException("Aborted", "AbortError")); }, { once: true });
    });
  }

  release(provider: string) {
    this.busy[provider]--;
    const i = this.waiters.findIndex((w) => w.provider === provider);
    if (i >= 0) this.waiters.splice(i, 1)[0].go();
  }
}

interface InFlight { promise: Promise<PartAudio>; ctrl: AbortController; tier: "flex" | "standard"; audioStarted: boolean }

class Engine {
  private slots = new Slots();
  private memory = new Map<string, PartAudio>();
  private inflight = new Map<string, InFlight>();
  status = "";

  async key(articleId: string, index: number, text: string, v: NarrationVoice): Promise<string> {
    return `${articleId}:${String(index).padStart(4, "0")}:${(await sha1(`${v.provider}:${v.model}:${v.voice}|${settings.narrationStyle}|${text}`)).slice(0, 16)}`;
  }

  async cached(key: string): Promise<PartAudio | null> {
    const m = this.memory.get(key);
    if (m) return m;
    const s = await db.get<Stored>("audio", key).catch(() => undefined);
    if (!s) return null;
    const a = { pcm: new Int16Array(s.pcm), timing: s.timing, rate: s.rate };
    this.remember(key, a);
    return a;
  }

  private remember(key: string, a: PartAudio) {
    this.memory.set(key, a);
    while (this.memory.size > 12) this.memory.delete(this.memory.keys().next().value!);
  }

  /** The part's audio: cached, or narrated now. `urgent` = playback is waiting for it (standard tier). */
  async part(articleId: string, index: number, text: string, v: NarrationVoice, urgent: boolean): Promise<PartAudio> {
    const key = await this.key(articleId, index, text, v);
    const hit = await this.cached(key);
    if (hit) return hit;
    const existing = this.inflight.get(key);
    if (existing) {
      // Flex → standard: a queued Flex request with no audio yet is cancelled and asked again on standard.
      if (!(urgent && existing.tier === "flex" && !existing.audioStarted)) return existing.promise;
      existing.ctrl.abort();
      this.inflight.delete(key);
    }
    const tier: "flex" | "standard" = !urgent && v.provider === "gemini" && settings.useFlex ? "flex" : "standard";
    const ctrl = new AbortController();
    const entry: InFlight = { ctrl, tier, audioStarted: false, promise: null as any };
    entry.promise = (async () => {
      const pieces: { text: string; pcm: Int16Array }[][] = await Promise.all(segments(text).map((seg) =>
        this.segment(seg, v, tier, urgent, ctrl.signal, () => { entry.audioStarted = true; })));
      const flat = pieces.flat();
      const total = flat.reduce((n, p) => n + p.pcm.length, 0);
      const pcm = new Int16Array(total);
      let o = 0;
      for (const p of flat) { pcm.set(p.pcm, o); o += p.pcm.length; }
      const audio: PartAudio = { pcm, timing: partTiming(text, flat, RATE), rate: RATE };
      this.remember(key, audio);
      await db.put("audio", key, { pcm: pcm.buffer, timing: audio.timing, rate: RATE } satisfies Stored).catch(() => {});
      return audio;
    })().finally(() => { if (this.inflight.get(key) === entry) this.inflight.delete(key); });
    this.inflight.set(key, entry);
    return entry.promise;
  }

  /** Cancels narration of an article's parts (e.g. it was deleted or another one started). */
  cancel(articleId: string, except?: number) {
    for (const [k, f] of this.inflight) {
      const [id, idx] = k.split(":");
      if (id === articleId && Number(idx) !== except) { f.ctrl.abort(); this.inflight.delete(k); }
    }
  }

  /** One segment with retries; a refused piece is split in half down to 150 characters. */
  private async segment(text: string, v: NarrationVoice, tier: "flex" | "standard", urgent: boolean, signal: AbortSignal,
    onAudio: () => void): Promise<{ text: string; pcm: Int16Array }[]> {
    let delay = 1000, network = 0, limited = 0;
    for (;;) {
      await this.slots.acquire(v.provider, urgent, signal);
      try {
        const pcm = await narrate(text, v, { tier, style: v.provider === "openrouter" ? "" : settings.narrationStyle, signal, onFirstAudio: onAudio });
        this.status = "";
        return [{ text, pcm }];
      } catch (e) {
        if (isAbort(e) || signal.aborted) throw e;
        const f = e instanceof TTSFailure ? e : new TTSFailure(String(e), "network");
        if ((f.kind === "network" || f.kind === "unavailable") && network < 4) { network++; await sleep(delay, signal); delay *= 2; continue; }
        if (f.kind === "rateLimited" && limited < 5) {
          limited++;
          this.slots.cooldownUntil[v.provider] = Date.now() + f.retryAfter * 1000;
          this.status = `${v.provider === "gemini" ? "Gemini" : v.provider === "openai" ? "OpenAI" : "OpenRouter"} rate limit, ${Math.round(f.retryAfter)} s`;
          continue;
        }
        if ((f.kind === "filtered" || f.kind === "rejected") && text.length > 150) {
          const halves = bisect(text);
          if (halves) {
            const [a, b] = await Promise.all(halves.map((h) => this.segment(h, v, tier, urgent, signal, onAudio)));
            return [...a, ...b];
          }
        }
        if (f.kind === "daily") throw new TTSFailure("Daily limit reached for this voice. Try another speech model, or tomorrow.", "fatal");
        throw f;
      } finally {
        this.slots.release(v.provider);
      }
    }
  }
}

export const engine = new Engine();

/** A WAV file of 16-bit mono PCM, for the <audio> element (pitch-preserving playback rate, media controls). */
export function wav(pcm: Int16Array, rate: number): Blob {
  const header = new ArrayBuffer(44);
  const d = new DataView(header);
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) d.setUint8(o + i, s.charCodeAt(i)); };
  str(0, "RIFF"); d.setUint32(4, 36 + pcm.byteLength, true); str(8, "WAVE"); str(12, "fmt ");
  d.setUint32(16, 16, true); d.setUint16(20, 1, true); d.setUint16(22, 1, true); d.setUint32(24, rate, true);
  d.setUint32(28, rate * 2, true); d.setUint16(32, 2, true); d.setUint16(34, 16, true); str(36, "data"); d.setUint32(40, pcm.byteLength, true);
  return new Blob([header, pcm.buffer as ArrayBuffer], { type: "audio/wav" });
}
